/**
 * The durable delayed-commit outbox (13-06, 13-RESEARCH Pattern 1): one-tap with undo, without
 * lying to the audit trail.
 *
 *   - A tap **enqueues** a verb result (13-04 `VerbResult`) and the UI hides the card.
 *   - **Undo** removes the entry. Nothing is ever written: `commit` is never called for it.
 *   - The write happens only when the caller **flushes**: when the undo toast closes (Sonner's
 *     `onAutoClose` / `onDismiss`), when the tab hides (`flushAll`), or on the next load
 *     (`replay`). There are NO timers in this module: Sonner pauses on hover, and a separate timer
 *     would commit while the user is still reading the undo.
 *   - **Replay never double-writes.** Every audit draft's `Note` carries `[a:<actionId>]` from
 *     enqueue, so a left-behind entry is checked against the audit list (`findCommitted`) before it
 *     is committed. A per-id in-flight guard stops two flushes of one action both writing.
 *
 * Further guarantees, each tested:
 *   - A 412 marks the entry `conflict` and takes it out of auto-retry (the UI restores the card,
 *     "changed by someone else"). It is kept, never silently dropped.
 *   - Once the PATCH has landed (`progress.patched()`), the entry keeps the audit drafts only and a
 *     retry posts only the audit rows not yet posted (RESEARCH anti-pattern 4).
 *   - Entries belong to `ownerUpn`. Nothing is committed under a different signed-in user (a shared
 *     desk must never write someone else's action under the wrong token); such entries stay stored.
 *   - Two tabs share one storage key. Each tab writes only its own entries (read-merge-write), a
 *     tab adopts another tab's entry only when it is stale, and a tab never commits or undoes an
 *     entry another tab has taken over.
 *   - Actions on the same item commit in order, and a later one inherits the eTag the earlier
 *     commit returned (otherwise Accept-then-Done on one card would always 412).
 *   - If storage throws, the outbox keeps working in memory and reports `durable: false`; the
 *     undo window is then 0 ms, so the UI says "saving immediately".
 *
 * `commit` is 13-07's (PATCH with If-Match, then the audit POSTs). Pure: no React, no Graph.
 */

import { UNDO_WINDOW_MS } from '../config/actionRegister';
import type { ActionVerb, AuditRowDraft } from '../types/actionRegister';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The single storage key: a JSON array of `OutboxEntry`. */
export const OUTBOX_STORAGE_KEY = 'qms.outbox.v1';

/** An entry held by another tab is adopted by `replay` only once it is this old (ms since held). */
export const DEFAULT_STALE_AFTER_MS = 60_000;

/** A synchronous key/value store. Its methods MAY throw; the outbox catches and goes in-memory. */
export interface OutboxStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

/**
 * `pending`: waiting for its window or a retry. `committing`: a commit is running.
 * `conflict`: the PATCH got 412; never retried automatically.
 */
export type OutboxState = 'pending' | 'committing' | 'conflict';

export interface OutboxEntry {
  /** Client uuid; also the `[a:<actionId>]` tag in every audit Note. */
  actionId: string;
  /** The Action Register item id. */
  itemId: number;
  /** Sent as `If-Match` on the PATCH. */
  eTag: string;
  verb: ActionVerb;
  /** The parent PATCH. Emptied once it has landed (`patched`). */
  patch: Record<string, unknown>;
  /** The audit drafts, each `Note` tagged `[a:<actionId>]`. */
  audit: AuditRowDraft[];
  /** When the human acted (ISO). Every draft's `EntryTimestamp`, even when committed late. */
  actedAt: string;
  /** When the undo window closes (ISO). `actedAt` itself when storage is not durable. */
  dueAt: string;
  /** Who acted. Only this signed-in user's token may commit it. */
  ownerUpn: string;
  state: OutboxState;
  attempts: number;
  lastError: string | null;
  lastStatus: number | null;
  /** The PATCH has landed; only the audit rows remain. */
  patched: boolean;
  /** Indices into `audit` already posted. */
  auditPosted: number[];
  /** The late-commit note has been appended (replay). */
  late: boolean;
  /** The outbox instance (tab) holding the entry, and since when (ISO). */
  heldBy: string;
  heldAt: string;
}

/** What a tap hands the outbox: a verb result plus the row it applies to. */
export interface EnqueueInput {
  itemId: number;
  eTag: string;
  verb: ActionVerb;
  patch: Record<string, unknown>;
  audit: AuditRowDraft[];
  ownerUpn: string;
  /** The tap time. Defaults to `now()`. */
  actedAt?: string | Date;
}

/** How `commit` reports partial progress, so a retry never repeats a write that landed. */
export interface CommitProgress {
  /** Call as soon as the PATCH returned 2xx. */
  patched(): void;
  /** Call as soon as audit draft `index` was posted (2xx). */
  auditPosted(index: number): void;
}

/** `eTag`: the item's new eTag from the PATCH response, for a later action on the same item. */
export interface CommitResult {
  eTag?: string;
}

/**
 * Writes one entry: unless `entry.patched`, PATCH `entry.patch` with `If-Match: entry.eTag` and
 * call `progress.patched()`; then POST each of `auditToPost(entry)` and call
 * `progress.auditPosted(index)` after each. Throw on failure; a 412 must carry its status
 * (`statusCode` or `status`).
 */
export type CommitFn = (
  entry: OutboxEntry,
  progress: CommitProgress,
) => Promise<CommitResult | void>;

export interface OutboxDeps {
  storage: OutboxStorage;
  /** Epoch ms. */
  now: () => number;
  commit: CommitFn;
  /** Is there an audit row whose `Note` contains `[a:<actionId>]`? (`entry` gives the ParentRef.) */
  findCommitted: (actionId: string, entry: OutboxEntry) => Promise<boolean>;
  /** The signed-in user's UPN; null / undefined when nobody is signed in. */
  currentUpn: () => string | null | undefined;
  /** The undo window when storage is durable. Default `UNDO_WINDOW_MS`. */
  windowMs?: number;
  /** Default `DEFAULT_STALE_AFTER_MS`. */
  staleAfterMs?: number;
  /** Default `crypto.randomUUID`. */
  randomId?: () => string;
  /** This tab's id. Default `randomId()`. */
  instanceId?: string;
}

/**
 * `committed`: written and removed. `missing`: not held here (undone, unknown, or removed by another
 * tab). `failed`: will be retried. `conflict`: 412. `blocked`: an earlier action on the same item
 * has not committed. `wrong-user`: owned by someone other than the signed-in user. `taken`: another
 * tab now holds it.
 */
export type FlushOutcome =
  | 'committed'
  | 'missing'
  | 'failed'
  | 'conflict'
  | 'blocked'
  | 'wrong-user'
  | 'taken';

export interface ReplayResult {
  /** Already in the audit list: removed, not committed. */
  alreadyCommitted: string[];
  /** Committed late. */
  committed: string[];
  /** Could not be checked or committed; kept for a retry. */
  failed: string[];
  /** Stored conflicts, adopted so the UI can say the action did not land. */
  conflicts: string[];
  /** Another user's entries: left stored. */
  skippedOtherUser: string[];
  /** Held by another live tab: left alone. */
  skippedLive: string[];
}

export interface Outbox {
  /** False when storage threw: entries live in memory only, and the window is 0 ms. */
  readonly durable: boolean;
  readonly instanceId: string;
  enqueue(input: EnqueueInput): string;
  /** True only if nothing was written. False → the UI offers Reopen instead. */
  undo(actionId: string): boolean;
  flush(actionId: string): Promise<FlushOutcome>;
  /** Flushes every pending entry, in enqueue order (on `visibilitychange → hidden`). */
  flushAll(): Promise<Record<string, FlushOutcome>>;
  /** Pending entries whose window has closed. */
  due(): OutboxEntry[];
  /** How many actions are still saving (for the `beforeunload` prompt). */
  pending(): number;
  /** On load (and when the tab becomes visible): finish what an earlier session left behind. */
  replay(): Promise<ReplayResult>;
  /** Snapshots of the entries this tab holds. */
  entries(): OutboxEntry[];
}

// ---------------------------------------------------------------------------
// Helpers (exported for 13-07)
// ---------------------------------------------------------------------------

/** The dedupe tag carried in every audit Note. */
export function actionTag(actionId: string): string {
  return `[a:${actionId}]`;
}

/** Does this audit Note carry the tag of `actionId`? (13-07's `findCommitted` scan.) */
export function noteHasAction(note: string | null | undefined, actionId: string): boolean {
  return typeof note === 'string' && note.includes(actionTag(actionId));
}

/** Appended to each audit Note committed by `replay`. */
export function lateNote(actedAt: string): string {
  return ` (committed late; acted ${actedAt})`;
}

/** The audit drafts still to post, with their indices (for `progress.auditPosted`). */
export function auditToPost(entry: OutboxEntry): Array<{ index: number; draft: AuditRowDraft }> {
  return entry.audit
    .map((draft, index) => ({ index, draft }))
    .filter(({ index }) => !entry.auditPosted.includes(index));
}

/** The HTTP status on a thrown error: Graph's `statusCode` or a fetch-style `status`. */
export function statusOf(err: unknown): number | null {
  if (typeof err !== 'object' || err === null) return null;
  const e = err as { statusCode?: unknown; status?: unknown };
  if (typeof e.statusCode === 'number') return e.statusCode;
  if (typeof e.status === 'number') return e.status;
  return null;
}

/** In-memory storage (tests, and a fallback). */
export function memoryStorage(): OutboxStorage {
  const data = new Map<string, string>();
  return {
    get: (k) => data.get(k) ?? null,
    set: (k, v) => void data.set(k, v),
    remove: (k) => void data.delete(k),
  };
}

/**
 * The production adapter over `localStorage`. Errors are deliberately NOT swallowed here: the
 * outbox catches them and reports `durable: false`. Even reaching `localStorage` can throw.
 */
export function browserStorage(): OutboxStorage {
  const ls = (): Storage => globalThis.localStorage;
  return {
    get: (k) => ls().getItem(k),
    set: (k, v) => ls().setItem(k, v),
    remove: (k) => ls().removeItem(k),
  };
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function sameUser(a: string | null | undefined, b: string | null | undefined): boolean {
  const x = (a ?? '').trim().toLowerCase();
  const y = (b ?? '').trim().toLowerCase();
  return x !== '' && x === y;
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

function toIso(value: string | Date | number): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) throw new TypeError(`outbox: invalid time ${String(value)}`);
  return d.toISOString();
}

function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

/** A stored row is used only if it has the fields a commit needs; anything else is ignored. */
function isEntry(x: unknown): x is OutboxEntry {
  if (typeof x !== 'object' || x === null) return false;
  const e = x as Partial<OutboxEntry>;
  return (
    typeof e.actionId === 'string' &&
    typeof e.itemId === 'number' &&
    typeof e.eTag === 'string' &&
    typeof e.ownerUpn === 'string' &&
    typeof e.actedAt === 'string' &&
    typeof e.heldBy === 'string' &&
    typeof e.heldAt === 'string' &&
    Array.isArray(e.audit) &&
    Array.isArray(e.auditPosted) &&
    typeof e.patch === 'object' &&
    e.patch !== null
  );
}

// ---------------------------------------------------------------------------
// The outbox
// ---------------------------------------------------------------------------

export function createOutbox(deps: OutboxDeps): Outbox {
  const { storage, now, commit, findCommitted, currentUpn } = deps;
  const windowMs = deps.windowMs ?? UNDO_WINDOW_MS;
  const staleAfterMs = deps.staleAfterMs ?? DEFAULT_STALE_AFTER_MS;
  const randomId = deps.randomId ?? (() => crypto.randomUUID());
  const instanceId = deps.instanceId ?? randomId();

  /** The entries this tab holds, in enqueue / adoption order. */
  const held = new Map<string, OutboxEntry>();
  const inFlight = new Map<string, Promise<FlushOutcome>>();
  let durable = true;

  const nowIso = (): string => toIso(now());

  // --- storage (every call guarded; any throw → in-memory for the rest of the session) ---

  function readStored(): OutboxEntry[] {
    if (!durable) return [];
    let raw: string | null;
    try {
      raw = storage.get(OUTBOX_STORAGE_KEY);
    } catch {
      durable = false;
      return [];
    }
    if (!raw) return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(isEntry) : [];
    } catch {
      return [];
    }
  }

  /** Read-merge-write: replace (or drop, when `next` is null) only this action's row. */
  function writeStored(actionId: string, next: OutboxEntry | null): void {
    if (!durable) return;
    const rows = readStored().filter((e) => e.actionId !== actionId);
    if (next) rows.push(next);
    if (!durable) return;
    try {
      storage.set(OUTBOX_STORAGE_KEY, JSON.stringify(rows));
    } catch {
      durable = false;
    }
  }

  const persist = (e: OutboxEntry): void => writeStored(e.actionId, e);
  const unpersist = (actionId: string): void => writeStored(actionId, null);

  /**
   * Is this tab still the holder of `actionId` in storage? When storage is not durable, memory is
   * the only truth and the answer is yes.
   */
  function stillMine(actionId: string): 'mine' | 'missing' | 'taken' {
    if (!durable) return 'mine';
    const row = readStored().find((e) => e.actionId === actionId);
    if (!durable) return 'mine';
    if (!row) return 'missing';
    return row.heldBy === instanceId ? 'mine' : 'taken';
  }

  // Probe once so the UI knows the window before the first tap.
  try {
    const raw = storage.get(OUTBOX_STORAGE_KEY);
    storage.set(OUTBOX_STORAGE_KEY, raw ?? '[]');
  } catch {
    durable = false;
  }

  // --- ordering on one item ---

  function earlierOnSameItem(e: OutboxEntry): OutboxEntry[] {
    const out: OutboxEntry[] = [];
    for (const x of held.values()) {
      if (x.actionId === e.actionId) break;
      if (x.itemId === e.itemId) out.push(x);
    }
    return out;
  }

  function laterOnSameItem(e: OutboxEntry): OutboxEntry[] {
    let seen = false;
    const out: OutboxEntry[] = [];
    for (const x of held.values()) {
      if (x.actionId === e.actionId) {
        seen = true;
        continue;
      }
      if (seen && x.itemId === e.itemId) out.push(x);
    }
    return out;
  }

  // --- enqueue ---

  function enqueue(input: EnqueueInput): string {
    if (!input || typeof input.ownerUpn !== 'string' || input.ownerUpn.trim() === '') {
      throw new TypeError('outbox: an entry needs its ownerUpn');
    }
    if (!Number.isFinite(input.itemId) || typeof input.eTag !== 'string' || !Array.isArray(input.audit)) {
      throw new TypeError('outbox: an entry needs itemId, eTag and audit[]');
    }
    const actionId = randomId();
    const actedAt = toIso(input.actedAt ?? now());
    const tag = actionTag(actionId);
    const audit = clone(input.audit).map((draft) => ({
      ...draft,
      entryTimestamp: actedAt,
      note: draft.note ? `${draft.note} ${tag}` : tag,
    }));
    const entry: OutboxEntry = {
      actionId,
      itemId: input.itemId,
      eTag: input.eTag,
      verb: input.verb,
      patch: clone(input.patch),
      audit,
      actedAt,
      dueAt: toIso(Date.parse(actedAt) + (durable ? windowMs : 0)),
      ownerUpn: input.ownerUpn.trim(),
      state: 'pending',
      attempts: 0,
      lastError: null,
      lastStatus: null,
      patched: false,
      auditPosted: [],
      late: false,
      heldBy: instanceId,
      heldAt: nowIso(),
    };
    held.set(actionId, entry);
    persist(entry);
    // If this very write is what failed, the entry exists only in memory: save immediately.
    if (!durable) entry.dueAt = actedAt;
    return actionId;
  }

  // --- undo ---

  function undo(actionId: string): boolean {
    const e = held.get(actionId);
    if (!e) return false;
    if (inFlight.has(actionId) || e.state === 'committing' || e.patched) return false;
    const where = stillMine(actionId);
    if (where !== 'mine') {
      held.delete(actionId);
      return false;
    }
    // A later action on the same item was built on top of this one, so it goes too.
    const later = laterOnSameItem(e);
    if (later.some((x) => inFlight.has(x.actionId) || x.state === 'committing' || x.patched)) {
      return false;
    }
    for (const x of [e, ...later]) {
      held.delete(x.actionId);
      unpersist(x.actionId);
    }
    return true;
  }

  // --- flush ---

  async function doFlush(actionId: string): Promise<FlushOutcome> {
    let e = held.get(actionId);
    if (!e) return 'missing';
    if (e.state === 'conflict') return 'conflict';
    if (!sameUser(e.ownerUpn, currentUpn())) return 'wrong-user';

    for (const x of earlierOnSameItem(e)) {
      await flush(x.actionId);
      if (held.has(x.actionId)) {
        e.lastError = `blocked: an earlier action on this item (${x.actionId}) has not committed`;
        persist(e);
        return 'blocked';
      }
    }
    e = held.get(actionId);
    if (!e) return 'missing';

    const where = stillMine(actionId);
    if (where !== 'mine') {
      held.delete(actionId);
      return where;
    }

    const entry = e;
    entry.state = 'committing';
    entry.heldAt = nowIso();
    persist(entry);

    const progress: CommitProgress = {
      patched() {
        entry.patched = true;
        entry.patch = {};
        persist(entry);
      },
      auditPosted(index: number) {
        if (!entry.auditPosted.includes(index)) entry.auditPosted.push(index);
        persist(entry);
      },
    };

    try {
      const result = await commit(clone(entry), progress);
      held.delete(actionId);
      unpersist(actionId);
      const eTag = result && typeof result.eTag === 'string' ? result.eTag : null;
      if (eTag) {
        for (const x of held.values()) {
          if (x.itemId === entry.itemId && !x.patched) {
            x.eTag = eTag;
            persist(x);
          }
        }
      }
      return 'committed';
    } catch (err) {
      const status = statusOf(err);
      entry.attempts += 1;
      entry.lastError = messageOf(err);
      entry.lastStatus = status;
      entry.state = status === 412 && !entry.patched ? 'conflict' : 'pending';
      persist(entry);
      return entry.state === 'conflict' ? 'conflict' : 'failed';
    }
  }

  function flush(actionId: string): Promise<FlushOutcome> {
    const running = inFlight.get(actionId);
    if (running) return running;
    const p = doFlush(actionId).finally(() => inFlight.delete(actionId));
    inFlight.set(actionId, p);
    return p;
  }

  async function flushAll(): Promise<Record<string, FlushOutcome>> {
    const out: Record<string, FlushOutcome> = {};
    const ids = [...held.values()].filter((e) => e.state !== 'conflict').map((e) => e.actionId);
    for (const id of ids) {
      if (!held.has(id)) continue;
      out[id] = await flush(id);
    }
    return out;
  }

  // --- queries ---

  function due(): OutboxEntry[] {
    const t = now();
    return [...held.values()]
      .filter((e) => e.state === 'pending' && Date.parse(e.dueAt) <= t)
      .map(clone);
  }

  function pending(): number {
    return [...held.values()].filter((e) => e.state !== 'conflict').length;
  }

  function entries(): OutboxEntry[] {
    return [...held.values()].map(clone);
  }

  // --- replay ---

  function markLate(e: OutboxEntry): void {
    if (e.late) return;
    const suffix = lateNote(e.actedAt);
    e.audit = e.audit.map((draft, index) =>
      e.auditPosted.includes(index)
        ? draft
        : { ...draft, entryTimestamp: e.actedAt, note: `${draft.note ?? ''}${suffix}` },
    );
    e.late = true;
  }

  async function replay(): Promise<ReplayResult> {
    const result: ReplayResult = {
      alreadyCommitted: [],
      committed: [],
      failed: [],
      conflicts: [],
      skippedOtherUser: [],
      skippedLive: [],
    };
    const t = now();
    for (const s of readStored()) {
      if (held.has(s.actionId)) continue;
      if (!sameUser(s.ownerUpn, currentUpn())) {
        result.skippedOtherUser.push(s.actionId);
        continue;
      }
      const heldAt = Date.parse(s.heldAt);
      if (s.heldBy !== instanceId && Number.isFinite(heldAt) && t - heldAt < staleAfterMs) {
        result.skippedLive.push(s.actionId);
        continue;
      }

      // Adopt (claim) it before any await, so no other tab takes it at the same time.
      const e: OutboxEntry = {
        ...clone(s),
        state: s.state === 'conflict' ? 'conflict' : 'pending',
        heldBy: instanceId,
        heldAt: toIso(t),
      };
      held.set(e.actionId, e);
      persist(e);

      if (e.state === 'conflict') {
        result.conflicts.push(e.actionId);
        continue;
      }

      if (!e.patched) {
        let already: boolean;
        try {
          already = await findCommitted(e.actionId, clone(e));
        } catch (err) {
          // Unknown whether it landed: never commit blind. Hand it back as it was.
          held.delete(e.actionId);
          writeStored(e.actionId, { ...s, lastError: `replay check failed: ${messageOf(err)}` });
          result.failed.push(e.actionId);
          continue;
        }
        if (already) {
          held.delete(e.actionId);
          unpersist(e.actionId);
          result.alreadyCommitted.push(e.actionId);
          continue;
        }
      }

      markLate(e);
      persist(e);
      const outcome = await flush(e.actionId);
      if (outcome === 'committed') result.committed.push(e.actionId);
      else if (outcome === 'conflict') result.conflicts.push(e.actionId);
      else result.failed.push(e.actionId);
    }
    return result;
  }

  return {
    get durable() {
      return durable;
    },
    instanceId,
    enqueue,
    undo,
    flush,
    flushAll,
    due,
    pending,
    replay,
    entries,
  };
}
